import React from 'react';
import StaticContentPage from '../components/StaticContentPage';
import { Shield, Users, Heart, MessageSquare, Lock, AlertCircle, CheckCircle } from 'lucide-react';

const GuidelineSection = ({ icon: Icon, title, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
        <Icon size={20} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="text-gray-600 leading-relaxed space-y-4 ml-1">
      {children}
    </div>
  </div>
);

const Guidelines = () => {
  return (
    <StaticContentPage title="Community Guidelines" lastUpdated="May 14, 2026">
      <div className="max-w-3xl mb-12">
        <p className="text-xl font-medium text-gray-700 leading-relaxed mb-6">
          Welcome to the Chooziq community.
        </p>
        <p className="text-gray-600 leading-relaxed">
          Chooziq is a realtime interaction and collaborative engagement platform built for communities, discussions, live events, workshops, classrooms, organizations, and collaborative experiences. These Community Guidelines are intended to help maintain a respectful, safe, collaborative, and engaging environment for all users.
        </p>
      </div>

      <GuidelineSection icon={Shield} title="1. Our Community Principles">
        <p>Chooziq is built around participation, collaboration, realtime engagement, respectful communication, and meaningful community interaction.</p>
        <p>We encourage users to contribute constructively and help create positive experiences for others.</p>
      </GuidelineSection>

      <GuidelineSection icon={Users} title="2. Respect Other Users">
        <p>Users must treat others with respect and professionalism. Do not:</p>
        <ul className="list-disc ml-6 space-y-2 text-sm">
          <li>Harass or intimidate others</li>
          <li>Engage in hate speech or discrimination</li>
          <li>Post abusive or threatening content</li>
          <li>Bully, target, or attack individuals or groups</li>
          <li>Disrupt communities or live sessions intentionally</li>
        </ul>
        <p className="italic">Respectful disagreement is permitted, but personal attacks are not.</p>
      </GuidelineSection>

      <GuidelineSection icon={AlertCircle} title="3. Keep Content Appropriate">
        <p>Content shared on Chooziq should remain appropriate for collaborative and professional environments. Do not post illegal content, explicit material, graphic violence, harmful misinformation, or spam.</p>
      </GuidelineSection>

      <GuidelineSection icon={MessageSquare} title="4. Use Realtime Features Responsibly">
        <p>Chooziq includes realtime interaction tools such as live Q&A, brainstorming boards, polls, and comments. Users should participate responsibly and avoid flooding sessions with repetitive content or manipulating engagement.</p>
      </GuidelineSection>

      <GuidelineSection icon={CheckCircle} title="5. Respect Community and Moderator Decisions">
        <p>Community administrators, organizers, and hosts may establish additional rules. Users are expected to respect moderator instructions and community-specific policies.</p>
      </GuidelineSection>

      <GuidelineSection icon={Lock} title="6. Protect Privacy and Personal Information">
        <p>Do not share or expose private personal information, confidential organizational data, or access credentials. Users are responsible for protecting their own privacy.</p>
      </GuidelineSection>

      <GuidelineSection icon={Shield} title="7. Intellectual Property and Content Ownership">
        <p>Only share content that you have the right to use. Do not upload copyrighted material without permission or impersonate individuals or organizations.</p>
      </GuidelineSection>

      <GuidelineSection icon={AlertCircle} title="8. No Spam or Platform Abuse">
        <p>Do not use Chooziq to distribute spam, conduct scams, or abuse platform systems. Automated abuse or attempts to disrupt the platform may result in immediate action.</p>
      </GuidelineSection>

      <GuidelineSection icon={Shield} title="9. Reporting Violations">
        <p>Users may report abusive behavior, inappropriate content, harassment, or policy violations. Reports are reviewed by moderators or the Chooziq team.</p>
      </GuidelineSection>

      <GuidelineSection icon={Shield} title="10. Enforcement and Moderation">
        <p>Chooziq may take action against accounts that violate these guidelines, including content removal, warnings, temporary or permanent suspension.</p>
      </GuidelineSection>

      <div className="bg-gray-50 p-10 rounded-[40px] border border-gray-100 mt-16 text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Contact and Support</h3>
        <p className="text-gray-600 mb-6">If you have questions regarding these Guidelines, please contact us.</p>
        <div className="flex flex-col items-center gap-2">
          <a href="mailto:support@chooziq.com" className="text-lg font-bold text-brand-indigo hover:underline">support@chooziq.com</a>
          <p className="text-sm font-bold text-gray-900 mt-4">Website: Chooziq</p>
        </div>
      </div>
    </StaticContentPage>
  );
};

export default Guidelines;
